import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import AppShell from "@/components/layout/AppShell";
import { useAdmin } from "@/components/auth/AdminGate";
import { LANGUAGES } from "@/lib/constants";
import MediaSettings from "@/components/settings/MediaSettings";
import PriceList from "@/components/settings/PriceList";
import ProviderKey from "@/components/settings/ProviderKey";
import VisualReviewSettings from "@/components/settings/VisualReviewSettings";
import ImagePlanSettings from "@/components/settings/ImagePlanSettings";

const IMAGE_NUM = ["budget_usd", "per_page_usd", "free_pages", "max_budget_usd", "qa_reserve_pct", "max_slots"];
import VisualRules from "@/components/review/VisualRules";
import { Card, Pick, Row } from "@/components/settings/parts";
import TestUsers from "@/components/settings/TestUsers";
import { BudgetBar } from "@/components/costs/BudgetBar";
import { usd } from "@/components/ai/useAiEdits";
import { errMsg, useApi } from "@/lib/api";

const CREATIVITY = [["conservativa", "Conservativa"], ["bilanciata", "Bilanciata"], ["creativa", "Creativa"]];

function ModelRows({ label, value, onChange, settings, testid }) {
  const prov = Object.keys(settings.models).map((p) => [p, settings.providers[p] || p]);
  return (
    <Row label={label.title} hint={label.hint}>
      <div className="flex flex-wrap gap-2">
        <Pick value={value.provider} onChange={(p) => onChange({ provider: p, model: settings.models[p][0] })} options={prov} testid={`${testid}-provider-select`} className="w-48" />
        <Pick value={value.model} onChange={(m) => onChange({ ...value, model: m })} options={(settings.models[value.provider] || []).map((m) => [m, m])} testid={`${testid}-model-select`} className="w-60" />
      </div>
    </Row>
  );
}

function MonthBudget({ limits }) {
  const api = useApi();
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/costs/summary").then(({ data }) => setB(data.budget)).catch(() => {}); }, [api]);
  if (!b) return null;
  return b.month_budget
    ? <div className="max-w-sm" data-testid="settings-month-budget"><BudgetBar spent={b.month_spent} budget={b.month_budget} warn={limits.warn_pct} testid="settings-month-budget-bar" /></div>
    : <p className="text-xs text-slate-500" data-testid="settings-month-budget">Speso questo mese: {usd(b.month_spent)} (nessun budget mensile)</p>;
}

function Usage() {
  const api = useApi();
  const [u, setU] = useState(null);
  useEffect(() => { api.get("/usage").then(({ data }) => setU(data)).catch(() => setU(false)); }, [api]);
  if (!u) return <p className="text-sm text-slate-500">{u === false ? "Statistiche non disponibili" : "Caricamento…"}</p>;
  const stats = [["Generazioni completate", u.generations], ["Modifiche AI applicate", u.ai_edits_applied], ["Chiamate AI", u.calls],
    ["Chiamate fallite", u.failed_calls], ["Token input", u.input_tokens.toLocaleString("it-IT")], ["Costo stimato totale", usd(u.cost_usd)]];
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {stats.map(([l, v]) => (
          <div key={l} className="rounded-lg border border-slate-200 p-4" data-testid={`usage-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-xs text-slate-500">{l}</p><p className="mt-1 text-xl font-semibold text-slate-900 tabular-nums">{v}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500" data-testid="usage-cost-note">{u.cost_note}</p>
    </>
  );
}

function Access() {
  const api = useApi();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/users").then(({ data }) => setD(data)).catch(() => setD(false)); }, [api]);
  if (!d) return <p className="text-sm text-slate-500">{d === false ? "Elenco non disponibile" : "Caricamento…"}</p>;
  return (
    <ul className="divide-y divide-slate-100" data-testid="access-list">
      {d.users.map((x) => (
        <li key={x.user_id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
          <span className="truncate text-slate-800">{x.email || x.user_id}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${x.role === "admin" ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-600"}`}>{x.role === "admin" ? "Amministratore" : "Senza accesso"}</span>
        </li>
      ))}
    </ul>
  );
}

const initForm = (s) => ({ app_name: s.app_name, ui_density: s.ui_density, ai: { provider: s.ai.provider, model: s.ai.model, light: s.ai.light }, generation: s.generation,
  limits: { max_operation_usd: String(s.limits.max_operation_usd), monthly_budget_usd: s.limits.monthly_budget_usd == null ? "" : String(s.limits.monthly_budget_usd), warn_pct: s.limits.warn_pct },
  visual_review: { ...s.visual_review, max_usd: String(s.visual_review.max_usd) }, images: Object.fromEntries(Object.entries(s.images).map(([k, v]) => [k, typeof v === "number" ? String(v) : v])) });

export default function Settings() {
  const api = useApi();
  const { settings, refreshSettings } = useAdmin();
  const [form, setForm] = useState(() => initForm(settings));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLim = (k, v) => setForm((f) => ({ ...f, limits: { ...f.limits, [k]: v } }));
  const num = (v) => Number(String(v).replace(",", "."));

  const save = async () => {
    setSaving(true);
    const l = form.limits;
    try {
      await api.put("/settings", { ...form, limits: { max_operation_usd: num(l.max_operation_usd), monthly_budget_usd: l.monthly_budget_usd === "" ? null : num(l.monthly_budget_usd), warn_pct: l.warn_pct },
        visual_review: { ...form.visual_review, max_usd: num(form.visual_review.max_usd) },
        images: Object.fromEntries(Object.entries(form.images).filter(([k]) => !["paid_enabled", "authorized_cap_usd"].includes(k)).map(([k, v]) => [k, IMAGE_NUM.includes(k) ? num(v) : v])) });
      await refreshSettings(); toast.success("Impostazioni salvate");
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  return (
    <AppShell>
      <div className="px-5 sm:px-8 lg:px-12 py-8 lg:py-12 max-w-4xl space-y-6" data-testid="settings-page">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-sky-700 font-medium">Configurazione</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Impostazioni</h1>
        </div>
        <Card title="Generale" testid="settings-general">
          <Row label="Nome applicazione"><Input value={form.app_name} onChange={(e) => set("app_name", e.target.value)} className="max-w-sm" data-testid="settings-app-name-input" /></Row>
          <Row label="Densità interfaccia"><Pick value={form.ui_density} onChange={(v) => set("ui_density", v)} options={[["comoda", "Comoda"], ["compatta", "Compatta"]]} testid="settings-density-select" /></Row>
        </Card>
        <Card title="Motore AI" desc="Il modello principale genera e modifica le demo; il modello leggero esegue le operazioni economiche (es. l'analisi di «Correggi con AI»)." testid="settings-ai">
          <ModelRows label={{ title: "Modello principale" }} value={form.ai} onChange={(v) => set("ai", { ...form.ai, ...v })} settings={settings} testid="settings" />
          <ModelRows label={{ title: "Modello leggero", hint: "Operazioni economiche" }} value={form.ai.light} onChange={(v) => set("ai", { ...form.ai, light: v })} settings={settings} testid="settings-light" />
        </Card>
        <Card title="Limiti e budget" desc="Controllati dal server prima di ogni operazione e prima di ogni singola chiamata AI. Al raggiungimento l'operazione viene bloccata e il lavoro già fatto resta salvato." testid="settings-limits">
          <Row label="Limite per singola operazione" hint="USD stimati"><Input type="number" min="0.5" step="0.5" value={form.limits.max_operation_usd} onChange={(e) => setLim("max_operation_usd", e.target.value)} className="max-w-[160px]" data-testid="settings-max-operation-input" /></Row>
          <Row label="Budget mensile AI" hint="Vuoto = nessun limite"><Input type="number" min="1" step="5" value={form.limits.monthly_budget_usd} onChange={(e) => setLim("monthly_budget_usd", e.target.value)} placeholder="Nessun limite" className="max-w-[160px]" data-testid="settings-monthly-budget-input" /></Row>
          <Row label={`Avviso al ${form.limits.warn_pct}% del budget`}><Slider value={[form.limits.warn_pct]} min={50} max={95} step={5} onValueChange={([v]) => setLim("warn_pct", v)} className="max-w-sm" data-testid="settings-warn-slider" /></Row>
          <Row label="Questo mese"><MonthBudget limits={form.limits} /></Row>
          <p className="text-[11px] text-slate-500">Il budget di un singolo progetto si imposta dal pannello «Progetto» dell'editor o da <Link to="/costs" className="text-sky-700 hover:underline">Costi e utilizzo</Link>.</p>
        </Card>
        <Card title="Preferenze di generazione" testid="settings-generation">
          <Row label="Creatività"><Pick value={form.generation.creativity} onChange={(v) => set("generation", { ...form.generation, creativity: v })} options={CREATIVITY} testid="settings-creativity-select" /></Row>
          <Row label={`Priorità: velocità ↔ qualità (${form.generation.priority})`}>
            <Slider value={[form.generation.priority]} min={0} max={100} step={5} onValueChange={([v]) => set("generation", { ...form.generation, priority: v })} className="max-w-sm" data-testid="settings-priority-slider" />
          </Row>
          <Row label="Lingua predefinita"><Pick value={form.generation.default_language} onChange={(v) => set("generation", { ...form.generation, default_language: v })} options={LANGUAGES.map((l) => [l.value, l.label])} testid="settings-language-select" /></Row>
        </Card>
        <Card title="Piano immagini dei siti" desc="Prima di generare, lo Studio calcola quali foto servono pagina per pagina, la fonte e il costo. Le immagini AI automatiche non sono ancora attive; il piano è già consultabile nel pannello «Progetto» dell'editor." testid="settings-images">
          <ImagePlanSettings value={form.images} onChange={(v) => set("images", v)} />
        </Card>
        <Card title="Revisione visiva" desc="Controllo qualità su screenshot reali desktop e mobile: l'analisi AI e le correzioni partono solo dopo la stima e la tua conferma, e ogni correzione va approvata nel prima/dopo." testid="settings-visual-review">
          <VisualReviewSettings value={form.visual_review} onChange={(v) => set("visual_review", v)} />
        </Card>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="settings-save-btn">{saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}Salva impostazioni</Button>
        </div>
        <div id="visual-rules" className="scroll-mt-6">
          <Card title="Regole apprese dalla revisione visiva" desc="Ogni difetto trovato viene registrato. Quando lo stesso tipo di difetto compare in 3 progetti diversi, lo Studio propone una regola per il prompt di generazione: entra nelle prossime demo solo se la approvi." testid="settings-visual-rules">
            <VisualRules />
          </Card>
        </div>
        <Card title="Chiavi API" desc="Ogni provider usa la Universal Key Emergent finché non scegli la tua chiave. Le chiavi sono cifrate sul server e non vengono mai mostrate per intero. Le immagini AI usano sempre la Universal Key." testid="settings-keys">
          {Object.keys(settings.keys).map((p) => <ProviderKey key={p} provider={p} k={settings.keys[p]} onChange={refreshSettings} />)}
        </Card>
        <Card title="Listino prezzi" desc="Usato per le stime prima di ogni operazione e per i consuntivi in «Costi e utilizzo»." testid="settings-pricing"><PriceList /></Card>
        <Card title="Immagini: foto stock e AI" desc="Ricerca di foto gratuite e generazione di immagini con AI, usate dalla duplicazione e dal pannello «Immagini del sito»." testid="settings-media"><MediaSettings /></Card>
        <Card title="Hosting dei siti" desc="Dove vengono pubblicati i siti compilati: Studio, emulatore locale di prova o il tuo account Cloudflare (token cifrato sul server)." testid="settings-hosting"
          aside={<Link to="/hosting" className="inline-flex items-center gap-1 text-sm text-sky-700 hover:underline" data-testid="settings-hosting-link">Apri<ArrowRight size={14} /></Link>} />
        <Card title="Utilizzo AI" testid="settings-usage" aside={<Link to="/costs" className="inline-flex items-center gap-1 text-sm text-sky-700 hover:underline" data-testid="settings-costs-link">Costi e utilizzo<ArrowRight size={14} /></Link>}><Usage /></Card>
        <Card title="Accessi" desc="Solo l'email amministratore configurata sul server può accedere alla dashboard." testid="settings-access"><Access /></Card>
        <TestUsers />
      </div>
    </AppShell>
  );
}
